import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

export const ROLES = {
    ADMIN: 'admin',
    SALES: 'sales',
    OPERATIONS: 'operations',
    MANAGEMENT: 'management',
};

export const ROLE_LABELS = {
    [ROLES.ADMIN]: 'Admin',
    [ROLES.SALES]: 'Sales',
    [ROLES.OPERATIONS]: 'Operations',
    [ROLES.MANAGEMENT]: 'Management',
};

export const ROLE_PERMISSIONS = {
    [ROLES.ADMIN]: [
        'view_all_quotes', 'create_quotes', 'edit_all_quotes', 'delete_quotes',
        'override_price', 'export_quotes', 'duplicate_quotes',
        'view_products', 'edit_products', 'export_products',
        'view_custom_products', 'create_custom_products', 'edit_custom_products', 'delete_custom_products',
        'view_customers', 'create_customers', 'edit_customers', 'delete_customers',
        'export_customers', 'manage_customer_migration',
        'view_challans', 'create_challans', 'edit_challans', 'delete_challans', 'export_challans', 'duplicate_challans',
        'use_sheet_optimizer', 'view_dashboard',
        'manage_users', 'import_data', 'import_images', 'manage_unmatched_images',
        'view_costing', 'manage_categories', 'manage_config',
    ],
    [ROLES.MANAGEMENT]: [
        'view_all_quotes', 'create_quotes', 'edit_all_quotes',
        'override_price', 'export_quotes', 'duplicate_quotes',
        'view_products', 'export_products',
        'view_custom_products', 'create_custom_products', 'edit_custom_products',
        'view_customers', 'create_customers', 'edit_customers', 'export_customers',
        'view_challans', 'create_challans', 'edit_challans', 'export_challans', 'duplicate_challans',
        'use_sheet_optimizer', 'view_dashboard',
        'view_costing',
    ],
    [ROLES.SALES]: [
        'view_own_quotes', 'create_quotes', 'edit_own_quotes',
        'override_price', 'export_quotes', 'duplicate_quotes',
        'view_products', 'export_products',
        'view_custom_products', 'create_custom_products', 'edit_custom_products',
        'view_customers', 'create_customers', 'edit_customers',
        'view_challans', 'create_challans', 'edit_challans', 'export_challans',
        'use_sheet_optimizer', 'view_dashboard',
    ],
    [ROLES.OPERATIONS]: [
        'view_all_quotes', 'view_products',
        'view_custom_products',
        'view_customers',
        'view_challans', 'export_challans',
        'use_sheet_optimizer', 'view_dashboard',
    ],
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState([]);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const res = await api.get('/auth/me');
            setUser(res.data);
            setPermissions(res.data.permissions || ROLE_PERMISSIONS[res.data.role] || []);
        } catch {
            localStorage.removeItem('token');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { checkAuth(); }, [checkAuth]);

    const login = async (email, password) => {
        const res = await api.post('/auth/login', { email, password });
        localStorage.setItem('token', res.data.token);
        await checkAuth();
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        setPermissions([]);
    };

    const can = (perm) => permissions.includes(perm);
    const canAny = (...perms) => perms.some(p => permissions.includes(p));
    const isAdmin = () => user?.role === ROLES.ADMIN;
    const canViewAllQuotes = () => can('view_all_quotes');
    const canEditQuote = (quote) => {
        if (can('edit_all_quotes')) return true;
        if (can('edit_own_quotes') && quote?.created_by === user?.id) return true;
        return false;
    };
    const canViewSection = (section) => {
        if (isAdmin() || user?.role === ROLES.MANAGEMENT) return true;
        if (section === 'pricing') return can('view_costing');
        if (section === 'packaging' || section === 'specifications') return user?.role !== ROLES.SALES;
        return true;
    };

    return (
        <AuthContext.Provider value={{
            user, loading, login, logout, checkAuth, permissions,
            can, canAny, isAdmin, canViewAllQuotes, canEditQuote, canViewSection,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
